<template>
  <div>
    <h2 class="title bold align--center margin__b--24">
      {{ t('literal.login') }}
    </h2>

    <sso-providers class="margin__b--18" />

    <template v-if="allowedAuthenticationForm">
      <form
        novalidate
        @submit.prevent="handleSubmitForm()"
      >
        <form-notification
          class="margin__b--12"
          :keys="error"
        />

        <fieldset>
          <label
            class="input__label"
            for="login_email"
          >
            {{ t('literal.email') }} *
          </label>

          <input-email
            id="login_email"
            class="margin__t--6"
            :required="true"
            autocomplete="email"
            :v="v$.email"
            v-model="user.email"
          />
        </fieldset>

        <fieldset class="margin__t--24">
          <label
            class="input__label"
            for="login_password"
          >
            {{ t('literal.password') }} *
          </label>

          <input-password
            id="login_password"
            class="margin__t--6"
            :required="true"
            autocomplete="current-password"
            :v="v$.password"
            v-model="user.password"
          />
        </fieldset>

        <button
          type="button"
          class="margin__t--12 link__color--prim link--naked"
          @click.prevent="emit('redirectTo', 'forgot-password')"
        >
          {{ t('literal.forgot_password') }}
        </button>

        <div class="d-flex justify-content-center margin__t--36">
          <app-save-button
            type="submit"
            class="btn__size--medium"
            :show-icon="false"
            :is-loading="authLoader"
            :is-disabled="ssoLoader"
          >
            {{ t('literal.log_in') }}
          </app-save-button>
        </div>
      </form>

      <hr class="separator separator__size--full color--dw margin__tb--24">

      <p class="align--center">
        {{ t('literal.dont_have_an_account_yet') }}

        <button
          type="button"
          class="link__color--prim link--naked bold"
          @click.prevent="emit('redirectTo', 'register')"
        >
          {{ t('literal.sign_up') }}
        </button>
      </p>
    </template>
  </div>
</template>

<script setup>
import { reactive, defineAsyncComponent, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useSsoStore } from '@/stores/SsoStore'
import { useAllowedFeatures } from '@/composables/app/useAllowedFeatures'
import { useI18n } from '@/vendors/i18n'
import useVuelidate from '@vuelidate/core'
import { required, email, minLength } from '@vuelidate/validators'

import SsoProviders from '&/molecules/SsoProviders'
const InputEmail = defineAsyncComponent(() => import('&/atoms/InputEmail'))
const InputPassword = defineAsyncComponent(() => import('&/atoms/InputPassword'))
const AppSaveButton = defineAsyncComponent(() => import('&/molecules/AppSaveButton'))
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))

const emit = defineEmits(['authenticated', 'redirectTo'])

const { t } = useI18n()
const { allowedAuthenticationForm } = useAllowedFeatures()
const { login, $resetStore } = useAuthenticationStore()
const { loader: authLoader, error } = storeToRefs(useAuthenticationStore())
const { loader: ssoLoader } = storeToRefs(useSsoStore())

const user = reactive({
  email: null,
  password: null
})

const rules = {
  email: { required, email },
  password: { required, minLength: minLength(8) }
}

const v$ = useVuelidate(rules, user)

const handleSubmitForm = () => {
  v$.value.$validate()

  if (v$.value.$invalid) return

  login({ params: user })
    .then(() => emit('authenticated'))
}

onUnmounted(() => $resetStore('error'))
</script>

<style scoped lang="scss">
.title {
  font-size: rem(14px);
}
</style>
